/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, TemplateRef } from '@angular/core'
import { SessionCacheStatus } from '@helios/state-management'
import { notApplicableHeader } from '../services/cache-load-listener/cache-load-listener'

/**
 * This component acts as a placeholder which displays a message placeholder until the sesson cache is in a 'ready' state.
 * A panel will use this as a convenience to help manage its own state, knowing that a fresh instance of the grid will be created each time its cache starts loading.
 * This way the panel doesn't have to manage making its grids wait until the right time to make requests - once the cache is ready, the panel's grid is created and it can make requests
 *
 * A consumer can also declare that the wrapped content is not applicable at all for the
 * active session (see `notApplicable`). That is a terminal state: the content is never
 * created, so it never issues a request, and the cache placeholders are never shown no
 * matter what the cache status does.
 */
@Component({
  selector: 'hs-cache-load-listener',
  styleUrls: ['cache-load-listener.component.scss'],
  templateUrl: 'cache-load-listener.component.html',
})
export class CacheLoadListenerComponent {
  @Input() content: TemplateRef<any> | null = null

  @Input() sessionId?: string
  @Input() accountIds?: number[]

  /**
   * When true the wrapped content is not applicable for the active session. A static
   * message card is rendered instead and the content is never created, so it never
   * requests data. Takes precedence over every cache state, including ERROR.
   *
   * Domain-neutral by design: the consumer decides what "not applicable" means and
   * supplies the reason via `notApplicableMessage`.
   */
  @Input() notApplicable = false

  /**
   * Body copy rendered in the not-applicable card. Only read when `notApplicable`
   * is true.
   */
  @Input() notApplicableMessage?: string

  /** Shared header for the not-applicable card. */
  readonly notApplicableHeader = notApplicableHeader

  @Input() set sessionCacheStatus(status: SessionCacheStatus) {
    this.handleStatusUpdate(status)
  }

  /**
   * Controls whether the grid is shown, or the 'Cache Loading...' placeholder
   * Ready means the cache status is PARTIAL or DONE, and the grid is able to make requests
   */
  cacheReady = false
  /**
   * Controls whether the 'Error' placeholder will show.
   */
  hasError = false

  handleStatusUpdate(status: SessionCacheStatus): void {
    switch (status) {
      case SessionCacheStatus.PARTIAL:
      case SessionCacheStatus.DONE:
      case SessionCacheStatus.POST_LOAD_DONE: {
        //at this point, we will add the panel and it can start requesting data from the backends
        this.cacheReady = true
        this.hasError = false
        break
      }
      case SessionCacheStatus.ERROR: {
        this.cacheReady = false
        this.hasError = true
        break
      }
      case SessionCacheStatus.STARTED:
      case SessionCacheStatus.UNKNOWN:
      default: {
        this.cacheReady = false
        this.hasError = false
        break
      }
    }
  }
}
